import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useLatest from "@-/com.npmjs.use-latest";
import { useIsNavigationFocused } from "@-/dev.improve.haniverse.contexts.primary-controls-strip.opaque-chrome";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { useUpdateUserSettings } from "@-/dev.improve.haniverse.lib.user.settings.update";
import type { PrimaryNavigationDestination } from "@-/dev.improve.haniverse.database.schemas.actor.schema._tables.user";
import type { PrimaryNavigationCustomizationController } from "#primary-navigation-customization-controller";
import {
  mergeEditedPrimaryNavigationDestinations,
  resolvePrimaryNavigationStripDestinations,
} from "#primary-navigation-strip-destinations";
import { arePrimaryNavigationStripOrdersEqual } from "#primary-navigation-strip-order";
import { useAvailablePrimaryNavigationDestinations } from "#use-primary-navigation-strip-destinations";

type PendingSave = {
  id: number;
  /** Set once the write succeeded; the saved order may still be on its way to the cache. */
  isWritten: boolean;
  order: readonly PrimaryNavigationDestination[];
};

/**
 * Holds an edit session open while the calling page is focused, so the strip turns into its
 * editor, and persists every edit to the actor `user` row (which syncs across devices).
 *
 * The edited order is shown immediately; the saved order is only shown again once it is neither
 * older than an edit still being written nor identical to it. A failed save reverts the strip to
 * the saved order and reports `hasSaveError`.
 */
export function usePrimaryNavigationCustomizationSession(
  controller: PrimaryNavigationCustomizationController,
): { hasSaveError: boolean } {
  const isFocused = useIsNavigationFocused();
  const { primaryNavigationDestinations: stored } = useActorUser();
  const available = useAvailablePrimaryNavigationDestinations();
  const saved = useMemo(
    () => resolvePrimaryNavigationStripDestinations(stored, available),
    [available, stored],
  );
  const update = useUpdateUserSettings();
  const [hasSaveError, setHasSaveError] = useState(false);
  const pendingSaveRef = useRef<PendingSave | null>(null);
  const lastSaveIdRef = useRef(0);
  const latest = useLatest({ available, saved, stored, update });

  const onCommit = useCallback(
    (order: readonly PrimaryNavigationDestination[]) => {
      const current = latest.current;
      if (
        pendingSaveRef.current === null &&
        arePrimaryNavigationStripOrdersEqual(order, current.saved)
      ) {
        return;
      }
      lastSaveIdRef.current += 1;
      const id = lastSaveIdRef.current;
      pendingSaveRef.current = { id, isWritten: false, order };
      setHasSaveError(false);
      // Writes go out in edit order and each replaces the whole setting, so the last edit wins.
      current.update.mutate(
        {
          primaryNavigationDestinations: mergeEditedPrimaryNavigationDestinations(
            current.stored,
            order,
            current.available,
          ),
        },
        {
          onError: () => {
            if (pendingSaveRef.current?.id !== id) {
              return;
            }
            pendingSaveRef.current = null;
            setHasSaveError(true);
            controller.showOrder(latest.current.saved);
          },
          onSuccess: () => {
            const pending = pendingSaveRef.current;
            if (pending?.id === id) {
              pendingSaveRef.current = { ...pending, isWritten: true };
            }
          },
        },
      );
    },
    [controller, latest],
  );

  useEffect(() => {
    if (!isFocused) {
      return;
    }
    return controller.openSession({ available, onCommit });
  }, [available, controller, isFocused, onCommit]);

  useEffect(() => {
    const pending = pendingSaveRef.current;
    if (pending !== null) {
      if (arePrimaryNavigationStripOrdersEqual(saved, pending.order)) {
        pendingSaveRef.current = null;
        return;
      }
      if (!pending.isWritten) {
        // An older write (or the pre-edit value) is still arriving; the edit stays on screen.
        return;
      }
      // The edit was written, then something newer (another device) replaced it.
      pendingSaveRef.current = null;
    }
    controller.showOrder(saved);
  }, [controller, isFocused, saved]);

  return { hasSaveError };
}
